import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      duration: 0.5,
      ease: 'easeInOut' 
    } 
  },
  exit: { 
    opacity: 0,
    transition: { 
      duration: 0.5,
      ease: 'easeInOut' 
    } 
  }
};

const panelVariants = {
  hidden: { x: '100%' },
  visible: {
    x: 0,
    transition: { 
      type: 'spring', 
      stiffness: 200, 
      damping: 26,
      mass: 1.2,
      duration: 0.8 
    }
  },
  exit: {
    x: '100%',
    transition: { 
      duration: 0.6,
      ease: [0.32, 0.72, 0.35, 1.0]
    }
  }
};

let isOffCanvasOpen = false;
let currentData = null;
let openCallback = null;
let closeCallback = null;
let setIsOpenState = null;

const OffCanvas = ({
  title = '',
  children,
  width = '460px',
  showClose = true,
  onOpen,
  onClose
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  useEffect(() => {
    setIsOpenState = setIsOpen;
    openCallback = onOpen;
    closeCallback = onClose;
    
    return () => {
      setIsOpenState = null;
      openCallback = null;
      closeCallback = null;
    };
  }, [onOpen, onClose]);
  
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      if (scrollY) window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }
    
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
    };
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    isOffCanvasOpen = false;
    
    if (closeCallback) {
      closeCallback();
    }
  };

  useEffect(() => {
    setIsOpen(isOffCanvasOpen);
  }, []);

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <motion.div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={close}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          />

          <motion.div
            className="relative bg-white shadow-2xl rounded-2xl h-[calc(100%-4rem)] my-8 mr-6 w-full flex flex-col overflow-hidden"
            style={{ maxWidth: width }}
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="flex items-center justify-end px-6 pt-3">
              {showClose && (
                <motion.button
                  onClick={close}
                  className="text-gray-500 hover:text-gray-700 p-1"
                  aria-label="Fechar"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ 
                    opacity: 1, 
                    scale: 1,
                    transition: { delay: 0.4, duration: 0.4 } 
                  }}
                >
                  <FiX size={22} />
                </motion.button>
              )}
            </div>
            <motion.div 
              className="flex-1 overflow-y-auto p-4 space-y-6"
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: 1,
                transition: { delay: 0.4, duration: 0.6 } 
              }}
            >
              {children}
            </motion.div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

OffCanvas.open = (data) => {
  currentData = data;
  isOffCanvasOpen = true;
  
  if (setIsOpenState) {
    setIsOpenState(true);
  }
  
  if (openCallback && currentData) {
    openCallback(currentData);
  }
};

OffCanvas.close = () => {
  isOffCanvasOpen = false;
  
  if (setIsOpenState) {
    setIsOpenState(false);
  }
  
  if (closeCallback) {
    closeCallback();
  }
};

OffCanvas.init = () => {
  isOffCanvasOpen = false;
  currentData = null;
};

export default OffCanvas;